import { Injectable, inject } from '@angular/core';

import Echo from 'laravel-echo';

import { Store } from '@ngrx/store';
import { defer, Observable, Subject } from 'rxjs';

import { environment } from '@qwyk/portals/environment';

import { NotificationsEntity } from '../models/notifications';
import * as NotificationsActions from '../state/notifications.actions';

@Injectable({
    providedIn: 'root',
})
export class PortalsSocketNotificationsService {
    private readonly store = inject(Store);

    private initialized = false;
    private connected = false;
    private userId: string | null = null;
    private token: string | null = null;
    private privateChannel: string | null = null;
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    private echo!: Echo<any>;

    public registerEchoListener(
        token: string,
        userId: string
    ): Observable<NotificationsEntity> {
        if (this.initialized) {
            this.deregisterEchoListener();
        }

        const value = new Subject<NotificationsEntity>();

        this.userId = userId;
        this.token = token;
        this.privateChannel = `${environment.echoConfig.userModel}.${this.userId}`;
        const privateChannel = this.privateChannel;
        this.echo = new Echo({
            broadcaster: 'pusher',
            key: environment.echoConfig.options.key,
            cluster: environment.echoConfig.options.cluster,
            authEndpoint: environment.echoConfig.options.authEndpoint,
            auth: {
                headers: {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`,
                },
            },
            forceTLS: environment.echoConfig.options.forceTLS,
            disableStats: true,
            enabledTransports: environment.echoConfig.options.enabledTransports,
        });

        defer(async () => {
            await this.echo
                .private(privateChannel)
                .notification((notification: NotificationsEntity) => {
                    value.next(notification);
                });
            this.initialized = true;
        }).subscribe();

        this.subscribeEchoConnectionState();

        return value.asObservable();
    }

    public deregisterEchoListener(): void {
        if (!this.echo) {
            return;
        }

        this.initialized = false;
        this.echo.leave(
            `private-${environment.echoConfig.userModel}.${this.userId}`
        );
        this.echo.disconnect();
        this.connected = false;
        this.userId = null;
        this.token = null;
        this.privateChannel = null;
    }

    private subscribeEchoConnectionState(): void {
        const connection = this.echo.connector.pusher.connection;

        connection.bind('connected', () => {
            this.connected = true;
        });
        connection.bind('disconnected', () => {
            this.connected = false;

            if (this.initialized && this.token && this.userId) {
                this.store.dispatch(
                    NotificationsActions.initializeEchoListener({
                        token: this.token,
                        userId: this.userId,
                    })
                );
            }
        });
    }
}
